import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

const CustomerFilterSchema = z.object({
  senderPatterns: z
    .array(z.string())
    .optional()
    .describe("Patterns to match against sender address"),
  subjectPatterns: z
    .array(z.string())
    .optional()
    .describe("Patterns to match against email subject"),
  bodyPatterns: z
    .array(z.string())
    .optional()
    .describe("Patterns to match against email body"),
});

/**
 * Filter Email by Customer Step
 *
 * A generic, reusable step that applies customer-specific pattern matching
 * to determine if an inbound email matches expected criteria (e.g. is an 849 response).
 *
 * The step looks up the identified customer name in its `customerFilters` config
 * to find the right set of sender/subject/body patterns to apply.
 *
 * Pattern matching:
 * - Each pattern is tried as regex first (case-insensitive), falling back to substring match
 * - Within each field group, patterns use OR logic (any pattern can match)
 * - Across field groups, only configured groups are checked (AND logic)
 * - If a customer has no entry in the config, `unmatchedCustomerAction` determines behavior
 */
export const filterEmailByCustomerStep = defineStepConfig({
  type: "filter_email_by_customer",
  name: "Filter Email by Customer",
  description:
    "Apply customer-specific sender, subject, and body patterns to filter inbound emails. Each customer can have its own detection rules.",
  category: "Transformation",
  deprecated: {
    reason: "Use Filter Email which supports the same pattern matching",
    replacedBy: "filter_email",
  },

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email to filter",
    ),
    customerName: z
      .string()
      .nullable()
      .describe(
        "Customer name from the identify step, used to look up filter patterns",
      ),
  }),

  outputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "Pass-through of the inbound email",
    ),
    matched: z
      .boolean()
      .describe("Whether the email matched the customer's filter criteria"),
    confidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Confidence score (1.0 if matched, 0.0 if not)"),
    matchDetails: z
      .string()
      .describe(
        "Human-readable summary of which patterns matched or why the email was filtered out",
      ),
  }),

  configSchema: z.object({
    customerFilters: z
      .record(z.string(), CustomerFilterSchema)
      .optional()
      .describe(
        "Map of customer name → filter patterns. The customer name must match the output of the Identify Customer step.",
      ),
    unmatchedCustomerAction: z
      .enum(["skip", "pass"])
      .default("skip")
      .describe(
        "What to do when the customer is not found in customerFilters. 'skip' = matched:false, 'pass' = matched:true.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 seconds",
    retry: {
      maximumAttempts: 2,
    },
  },
});
